import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import Modal from 'src/app/components/Modal';
import { checkExerciseAtom } from 'src/app/state/atoms';
import styles from './ResumeConversationModal.module.scss';
import { MdClose } from 'react-icons/md';

interface ResumeConversationModalProps {
  onResume?: () => void;
  onStartOver: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

export const ResumeConversationModal: React.FC<ResumeConversationModalProps> = ({
  onResume,
  onStartOver,
  onSelectConversation,
}) => {
  const [checkExercise] = useRecoilState(checkExerciseAtom);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const newChatBtnRef = useRef<HTMLButtonElement>(null);
  const resumeBtnRef = useRef<HTMLButtonElement>(null);

  // Lock body scroll and handle Escape while modal is open
  // Tab order: Resume → New chat → Close (via tabIndex); Tab after Close leaves the chatbot
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onStartOver();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onStartOver]);

  useEffect(() => {
    const lastConversation = checkExercise?.data?.conversation_ids?.at(0);

    if (lastConversation) {
      setSelectedConversationId(lastConversation?.id);
    }
  }, [checkExercise]);

  // Focus Resume when available; otherwise New Chat (not Close)
  useEffect(() => {
    if (selectedConversationId) {
      resumeBtnRef.current?.focus();
    } else {
      newChatBtnRef.current?.focus();
    }
  }, [selectedConversationId]);

  const handleResume = () => {
    if (selectedConversationId && onSelectConversation) {
      onSelectConversation(selectedConversationId);
    }
    if (onResume) onResume();
  };

  return (
    <Modal>
      <div className={styles.backdrop} role='presentation' aria-hidden='false'>
        <div
          ref={modalRef}
          className={styles.modal}
          role='dialog'
          aria-modal='true'
          aria-labelledby='resume-conversation-title'
          aria-describedby='resume-conversation-desc'
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <button
                className={styles.closeBtn}
                onClick={onStartOver}
                aria-label={t('CLOSE')}
                type='button'
                tabIndex={3}
              >
                <MdClose size='1.5em' />
              </button>

              <h3 id='resume-conversation-title' className={styles.title}>
                {t('SELECT_CONVERSATION')}
              </h3>

              <p id='resume-conversation-desc' className={styles.subtitle}>
                {t('RESUME_MODAL_SUBTITLE')}
              </p>
            </div>

            <div className={styles.actions}>
              <button
                ref={newChatBtnRef}
                className={styles.secondaryBtn}
                onClick={onStartOver}
                type='button'
                aria-label={t('NEW_CHAT')}
                tabIndex={2}
              >
                {t('NEW_CHAT')}
              </button>

              <button
                ref={resumeBtnRef}
                className={styles.primaryBtn}
                onClick={handleResume}
                disabled={!selectedConversationId}
                type='button'
                aria-label={t('RESUME')}
                tabIndex={1}
              >
                {t('RESUME')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
